<template>
  <div>
    <div v-if="loadingSignature" class="mt-3">
      <b-card>
        <b-row>
          <b-col>
            <b-skeleton width="85%" />
            <b-skeleton width="55%" />
          </b-col>
          <b-col>
            <b-skeleton width="70%" />
            <b-skeleton width="55%" />
          </b-col>
          <b-col>
            <b-skeleton width="85%" />
            <b-skeleton width="55%" />
          </b-col>
          <b-col>
            <b-skeleton width="70%" />
            <b-skeleton width="55%" />
          </b-col>
          <b-col>
            <b-skeleton type="button" width="100%" />
          </b-col>
        </b-row>
      </b-card>
    </div>
    <template v-else>
      <sign-plan
        ref="signPlanRef"
        class="my-4"
        :disabled="isElectronicDisabled"
        :is-electronic-signature.sync="model.isElectronicSignature"
        :question-label="questionLabel"
        @change="handleSelectMechanism"
      />
      <div class="mt-4">
        <div v-if="hasSelectMechanisManuscript">
          <section-title has-line title="Documento" />
          <div
            v-if="document"
            class="row bg-secondary-3 rounded p-3 mb-3 ml-1 mr-0"
          >
            <div class="col-md-6">
              <div class="color-gray-6 font-14 font-weight-normal">
                Tipo de Documento:
              </div>
              <div class="color-black font-16 font-weight-normal">
                {{ document.documentTypeName }}
              </div>
            </div>
            <div class="col-md-6 d-flex justify-content-center">
              <div v-if="hasManuscriptDocUploaded && !editingDocument">
                <action-button
                  class=" mt-1"
                  i="fas fa-file-download text-white mr-n1 pr-1"
                  variant="success"
                  @click="downloadDocument"
                />
              </div>
              <div v-else-if="hasDownloadedTemplate || editingDocument">
                <span class="color-gray-6 font-14 font-weight-normal mr-3 mt-2">
                  Documento:
                </span>
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true }"
                >
                  <custom-file-input
                    ref="documentInform"
                    v-model="document.selectedDocument"
                    :accept="'.pdf'"
                    :attachment-id="document.documentId"
                    custom-error-msg="Favor de anejar un documento."
                    display-style="normal"
                    :error="errors[0]"
                    label-position="left"
                    required
                    @input="onSaveSignedDocument($event)"
                    :on-download="downloadDocument"
                  />
                </validation-provider>
              </div>
              <action-button
                v-else
                class="mt-1"
                i="fas fa-file-download text-white mr-n1 pr-1"
                variant="success"
                @click="downloadTemplate"
              />
            </div>
          </div>
        </div>
        <signature-hub-info
          v-else-if="hasSelectMechanismElectronic"
          :signature-info="signatureHubInfo"
          @configure-sign="handleConfigureSignatures"
        />
      </div>
    </template>
  </div>
</template>

<script>
import alert from '@/mixin/alert.mixin';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';

import { SignPlan } from '@/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';
import {
  getSignatureHubInfo as _getSignatureHubInfo,
  removeSignatureHub as _removeSignatureHub,
} from '@/services/signatureHub/api';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import digitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus.js';
import DocumentsService from '@/services/documents';
import reportService from '@/services/report';
import CustomFileInput from 'CustomFileInput';
import objectToFormData from 'object-to-formdata';

export default {
  name: 'SignatureTab',
  mixins: [alert],
  components: {
    SectionTitle,
    ActionButton,
    SignPlan,
    CustomFileInput,
    SignatureHubInfo,
  },
  props: {
    model: {
      type: Object,
      required: true,
      default: () => ({
        isElectronicSignature: null,
      }),
    },
    documentType: {
      type: Object,
      required: true,
      default: () => ({
        name: null,
        typeId,
      }),
    },
    questionLabel: {
      type: String,
      required: true,
      default: () =>
        '¿Cuál será el mecanismo que se estará utilizando para firmar?',
    },
    saveMechanism: {
      type: Function,
      default: () => Promise.resolve({}),
      required: true,
    },
    saveSignedDocument: {
      type: Function,
      default: () => Promise.resolve({}),
      required: true,
    },
  },
  data() {
    return {
      isReadOnly: false,
      planTypes: [],
      loadingSignature: false,
      canViewSignedTab: false,
      signatureHubInfo: null,
      document: {
        documentTypeName: null,
        documentTypeId: null,
        selectedDocument: null,
        documentId: null,
      },
      hasDownloadedTemplate: false,
      editingDocument: false,
      selectedMechanismSignPlan: null,
    };
  },
  computed: {
    hasSelectMechanisManuscript() {
      return this.model.isElectronicSignature === false;
    },
    hasSelectMechanismElectronic() {
      return this.model.isElectronicSignature === true;
    },
    hasManuscriptDocUploaded() {
      return !!this.model.signedDocumentId;
    },
    isElectronicDisabled() {
      return [
        this.isElectronicSigned,
        this.isElectronicPending,
        this.hasManuscriptDocUploaded,
      ].includes(true);
    },
    isElectronicPending() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        digitalSignatureDocumentStatus.InSigningProcess
      );
    },
    isElectronicSigned() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        digitalSignatureDocumentStatus.Signed
      );
    },
  },
  async mounted() {
    this.document.documentTypeName = this.documentType.name;
    this.document.documentTypeId = this.documentType.typeId;
    this.selectedMechanismSignPlan =
      this.model.isElectronicSignature == null && this.model.document != null
        ? false
        : this.model.isElectronicSignature;
    if (this.hasSelectMechanismElectronic) await this.getSignatureHubInfo();
  },
  methods: {
    async getSignatureHubInfo() {
      try {
        this.loadingSignature = true;
        const { data } = await _getSignatureHubInfo(
          SignatureHubTypes.MEDICAL_PLAN_CONSENT,
          Number(this.model.id)
        );
        this.signatureHubInfo = data;
        this.loadingSignature = false;
      } catch (error) {
        this.loadingSignature = false;
        this.ShowToast(
          'Error',
          error.response.data.message
            ? error.response.data.message
            : 'No se pudo obtener la información de firma.',
          'error'
        );
      }
    },
    async handleSelectMechanism(selected) {
      const value = selected?.value;
      const previousValue = this.selectedMechanismSignPlan;

      const toManuscript = value === false && previousValue === true;
      const toElectronic = value === true && previousValue === false;

      let save = true;

      if (toElectronic && this.document?.selectedDocument) {
        save = await this.setToElectronic();
      } else if (toManuscript && this.signatureHubInfo?.signatureHubId) {
        save = await this.setToManuscript();
        if (save) await this.getSignatureHubInfo();
      }

      if (save) {
        await this.saveMechanism(this.model.id, value).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }
    },
    async onSaveSignedDocument(data) {
      const payload = objectToFormData(
        {
          id: this.model.id,
          file: data,
        },
        {
          indices: true,
          nullsAsUndefineds: true,
        }
      );

      try {
        await this.saveSignedDocument(payload);

        this.ShowToast('', 'Documento guardado exitosamente.', 'success');

        if (this.editingDocument) {
          this.editingDocument = false;
          return;
        }

        this.$emit('loadData', true);
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar guardar la información.',
          'error'
        );
      }
    },
    async handleConfigureSignatures() {
      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.MEDICAL_PLAN_CONSENT,
          relatedId: this.model.id,
          from: 'medicalPlan',
        },
      });
    },
    async setToElectronic() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        'Usted ya se encuentra en un proceso de configuración de firma Manuscrito. ¿Está seguro que desea cambiar la configuración a Electronica?',
        'Sí',
        'No'
      );
      if (!confirmed) {
        this.model.isElectronicSignature = false;
        this.$nextTick(() => this.$refs.documentInform.loadFile());
        return false;
      }
      this.document.selectedDocument = null;
      return true;
    },
    async setToManuscript() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        'Usted ya se encuentra en un proceso de configuración de firma digital. ¿Está seguro que desea cambiar la configuración a manuscrito?',
        'Sí',
        'No'
      );

      if (!confirmed) {
        this.model.isElectronicSignature = true;
        return false;
      }
      try {
        await _removeSignatureHub(
          SignatureHubTypes.MEDICAL_PLAN_CONSENT,
          this.model.id
        );
        this.signatureHubInfo = null;
        return true;
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
        return false;
      }
    },
    async downloadTemplate() {
      try {
        await reportService.getMedicalPlanConsent(this.model.id);
        this.hasDownloadedTemplate = true;
      } catch (err) {
        this.ShowToast('', err.response.data.message || err.message, 'error');
      }
    },
    async downloadDocument() {
      await DocumentsService.downloadFromServer(this.model.signedDocumentId);
    },
  },
};
</script>

<style scoped lang="scss">
.bg-icon-orange {
  width: auto !important;
}
.bg-icon-orange ::v-deep .dot {
  background-color: var(--orange) !important;
}

::v-deep .custom-tab .custom-tab-container {
  margin-bottom: 20px !important;
}
::v-deep .custom-tab .custom-tab-container .nav-item .nav-link {
  padding-right: 50px !important;
  padding-left: 30px !important;
}
::v-deep .custom-tab .custom-tab-container .nav-item .nav-link.active {
  border-bottom: 3px solid var(--orange) !important;
  color: var(--orange) !important;
  padding-right: 50px !important;
  padding-left: 30px !important;
}
</style>
